import AddIcon from "@mui/icons-material/Add";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FactoryIcon from "@mui/icons-material/Factory";
import FilterCenterFocusIcon from "@mui/icons-material/FilterCenterFocus";
import MergeIcon from "@mui/icons-material/Merge";
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { SelectableCellContent } from "components/MergePicker/styles";
import {
  InventoryOverlapResolutionStrategy,
  InventoryOverlapStorageLocationAttribute,
} from "generated-graphql/graphql";
import { groupBy } from "lodash-es";
import pluralize from "pluralize";
import { useState } from "react";

import {
  combinedMaxAmountPreview,
  findCombineConflicts,
  formatEntryAmount,
  InventoryOverlap,
  InventoryOverlapEntry,
  inventoryOverlapKey,
  InventoryOverlapResolutionState,
  isInventoryOverlapResolved,
  stateFieldConflictKey,
  storageLocationConflictKey,
} from "./inventoryOverlaps";

const ATTRIBUTE_LABELS: Record<InventoryOverlapStorageLocationAttribute, string> = {
  [InventoryOverlapStorageLocationAttribute.Pressure]: "Pressure",
  [InventoryOverlapStorageLocationAttribute.Temperature]: "Temperature",
};

function storageTypesLabel(storageTypes: string[], storageTypeDescription: string | null): string {
  const typesLabel = storageTypes.length > 0 ? storageTypes.join(", ") : "No storage type";
  return storageTypeDescription ? `${typesLabel} (${storageTypeDescription})` : typesLabel;
}

type SelectableOptionCellProps = {
  /** Accessible name; matches the old radio labels ("<value> (<chemicals>)"). */
  label: string;
  display: string;
  caption: string;
  selected: boolean;
  onSelect: () => void;
};

/**
 * A pick-by-clicking option, reusing the merge table's SelectableCellContent
 * so conflict picks look and behave like the cell selections on the Review
 * details step. The wrapper restores the radio role, accessible name, and
 * keyboard support the plain clickable cell doesn't provide.
 */
function SelectableOptionCell({
  label,
  display,
  caption,
  selected,
  onSelect,
}: SelectableOptionCellProps) {
  return (
    <Box
      role="radio"
      aria-checked={selected}
      aria-label={label}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === " " || event.key === "Enter") {
          event.preventDefault();
          onSelect();
        }
      }}
      sx={{ height: "100%", alignSelf: "stretch" }}
    >
      <SelectableCellContent isSelected={selected} style={{ flexDirection: "column" }}>
        <Typography variant="body2">{display}</Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          {caption}
        </Typography>
      </SelectableCellContent>
    </Box>
  );
}

type KeepColumnCellProps = {
  /**
   * Present on the column's header cell, which carries the radio semantics
   * and testid for the whole column. Value cells below it are click targets
   * only, so screen readers see exactly one radio per chemical.
   */
  radio?: { label: string; testId: string };
  selected: boolean;
  hovered: boolean;
  onSelect: () => void;
  onHoverChange: (hovered: boolean) => void;
  children: React.ReactNode;
};

/**
 * One cell of a "Keep one" chemical column. The whole column is a single
 * pick target: hover state lives on the card so every cell in the hovered
 * column highlights together, matching the Review details step's cells.
 */
function KeepColumnCell({
  radio,
  selected,
  hovered,
  onSelect,
  onHoverChange,
  children,
}: KeepColumnCellProps) {
  return (
    <Box
      role={radio ? "radio" : undefined}
      aria-checked={radio ? selected : undefined}
      aria-label={radio?.label}
      tabIndex={radio ? 0 : undefined}
      data-testid={radio?.testId}
      onClick={onSelect}
      onKeyDown={
        radio
          ? (event: React.KeyboardEvent) => {
              if (event.key === " " || event.key === "Enter") {
                event.preventDefault();
                onSelect();
              }
            }
          : undefined
      }
      sx={{ height: "100%" }}
    >
      <SelectableCellContent
        isSelected={selected}
        isHovered={hovered}
        onHoverChange={onHoverChange}
      >
        {children}
      </SelectableCellContent>
    </Box>
  );
}

type ResolveInventoryOverlapsStepProps = {
  inventoryOverlaps: InventoryOverlap[];
  resolutions: Record<string, InventoryOverlapResolutionState>;
  onStrategyChange: (key: string, strategy: InventoryOverlapResolutionStrategy) => void;
  onKeepSelect: (key: string, keepChemicalId: string) => void;
  onFieldChoice: (key: string, conflictKey: string, choice: string) => void;
  onBatchStrategyChange: (strategy: InventoryOverlapResolutionStrategy) => void;
  onBack: () => void;
  onMerge: () => void;
  isMerging: boolean;
  mergedChemicalName: string;
  affectedFacilityCount: number;
  candidateCount: number;
  autoMergeFacilityYearCount: number;
};

function renderStorageLocations(storageLocationNames: string[]) {
  if (storageLocationNames.length === 0) {
    return "—";
  }

  return storageLocationNames.map((storageLocationName) => (
    <Box key={storageLocationName}>{storageLocationName}</Box>
  ));
}

function renderKeepMergedAmount(selectedEntry: InventoryOverlapEntry | undefined) {
  if (!selectedEntry) {
    return (
      <>
        {"— "}
        <Typography variant="caption" color="text.secondary">
          select a chemical to keep
        </Typography>
      </>
    );
  }

  return (
    <>
      {formatEntryAmount(selectedEntry)}
      <Typography variant="caption" color="text.secondary">
        {` from ${selectedEntry.chemicalName}`}
      </Typography>
    </>
  );
}

function renderMergedAmount(
  inventoryOverlap: InventoryOverlap,
  resolution: InventoryOverlapResolutionState,
  selectedEntry: InventoryOverlapEntry | undefined
) {
  if (resolution.strategy === InventoryOverlapResolutionStrategy.Blank) {
    return (
      <>
        {"— "}
        <Typography variant="caption" color="text.secondary">
          left blank, flagged for review
        </Typography>
      </>
    );
  }
  if (resolution.strategy === InventoryOverlapResolutionStrategy.Combine) {
    return combinedMaxAmountPreview(inventoryOverlap);
  }
  return renderKeepMergedAmount(selectedEntry);
}

function renderMergedStorage(
  inventoryOverlap: InventoryOverlap,
  resolution: InventoryOverlapResolutionState,
  selectedEntry: InventoryOverlapEntry | undefined
) {
  if (resolution.strategy === InventoryOverlapResolutionStrategy.Blank) {
    return renderStorageLocations([]);
  }
  if (resolution.strategy === InventoryOverlapResolutionStrategy.Combine) {
    return renderStorageLocations(
      Array.from(new Set(inventoryOverlap.entries.flatMap((e) => e.storageLocationNames)))
    );
  }
  return renderStorageLocations(selectedEntry?.storageLocationNames ?? []);
}

type InventoryOverlapCardProps = {
  inventoryOverlap: InventoryOverlap;
  resolution: InventoryOverlapResolutionState;
  onStrategyChange: (strategy: InventoryOverlapResolutionStrategy) => void;
  onKeepSelect: (keepChemicalId: string) => void;
  onFieldChoice: (conflictKey: string, choice: string) => void;
};

function InventoryOverlapCard({
  inventoryOverlap,
  resolution,
  onStrategyChange,
  onKeepSelect,
  onFieldChoice,
}: InventoryOverlapCardProps) {
  const key = inventoryOverlapKey(inventoryOverlap);
  const isCombining = resolution.strategy === InventoryOverlapResolutionStrategy.Combine;
  const isKeeping = resolution.strategy === InventoryOverlapResolutionStrategy.Keep;
  const selectedEntry = inventoryOverlap.entries.find(
    (entry) => entry.chemicalId === resolution.keepChemicalId
  );
  const conflicts = findCombineConflicts(inventoryOverlap);

  const [hoveredChemicalId, setHoveredChemicalId] = useState<string | null>(null);

  // Shared by all three cells of a chemical's column so they select and
  // highlight as one target.
  const keepCellProps = (chemicalId: string) => ({
    selected: chemicalId === resolution.keepChemicalId,
    hovered: chemicalId === hoveredChemicalId,
    onSelect: () => onKeepSelect(chemicalId),
    onHoverChange: (hovered: boolean) => setHoveredChemicalId(hovered ? chemicalId : null),
  });

  // SelectableCellContent brings its own padding; the wrapped cells shrink
  // theirs so the clickable area fills the cell. height: "inherit" pairs with
  // the 1px row-height hack below to stretch the content to the tallest cell
  // in the row (same trick as MergePicker's StyledTableRow).
  const keepCellSx = isKeeping ? { p: 0.5, height: "inherit" } : undefined;
  const keepRowSx = isKeeping ? { height: "1px" } : undefined;

  // One shared grid template for every picker row in the conflicts section, so
  // the radio options line up in columns across attributes and state fields.
  const optionColumnCount = Math.max(
    2,
    ...conflicts.storageLocations.flatMap((location) =>
      location.conflicts.map((conflict) => conflict.options.length)
    ),
    ...conflicts.stateFieldConflicts.map((conflict) => conflict.options.length)
  );
  const conflictRowSx = {
    mt: 1,
    display: "grid",
    gridTemplateColumns: `180px repeat(${optionColumnCount}, minmax(0, 1fr))`,
    columnGap: 2,
    alignItems: "center",
  };
  // Rows under a storage-location header indent to read as its children; the
  // label column narrows by the same 24px so the option columns still line up
  // with the un-indented state-field rows.
  const indentedConflictRowSx = {
    ...conflictRowSx,
    pl: 3,
    gridTemplateColumns: `156px repeat(${optionColumnCount}, minmax(0, 1fr))`,
  };

  return (
    <Paper variant="outlined" data-testid={`inventory-overlap-${key}`} sx={{ p: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">{inventoryOverlap.reportingYear}</Typography>
          {inventoryOverlap.hasIdenticalAmounts && (
            <Chip icon={<ContentCopyIcon />} label="identical amounts" size="small" />
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            Resolution:
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={resolution.strategy}
            onChange={(_event, strategy) => {
              if (strategy) onStrategyChange(strategy);
            }}
            data-testid={`inventory-overlap-strategy-${key}`}
          >
            <ToggleButton value={InventoryOverlapResolutionStrategy.Keep}>
              <FilterCenterFocusIcon fontSize="small" sx={{ mr: 0.5 }} />
              Keep one
            </ToggleButton>
            {inventoryOverlap.hasSharedUnit ? (
              <ToggleButton value={InventoryOverlapResolutionStrategy.Combine}>
                <AddIcon fontSize="small" sx={{ mr: 0.5 }} />
                Combine
              </ToggleButton>
            ) : (
              <Tooltip title="Amounts must share a unit to combine — unit conversion is coming in a later release.">
                <span>
                  <ToggleButton value={InventoryOverlapResolutionStrategy.Combine} disabled>
                    <AddIcon fontSize="small" sx={{ mr: 0.5 }} />
                    Combine
                  </ToggleButton>
                </span>
              </Tooltip>
            )}
            <ToggleButton value={InventoryOverlapResolutionStrategy.Blank}>
              <CheckBoxOutlineBlankIcon fontSize="small" sx={{ mr: 0.5 }} />
              Leave blank
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>
      {/* Fixed layout keeps column widths stable as selections change cell content. */}
      <Table size="small" sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow sx={keepRowSx}>
            <TableCell sx={{ width: "12%" }} />
            {inventoryOverlap.entries.map((entry) => (
              <TableCell key={entry.chemicalId} sx={keepCellSx}>
                {isKeeping ? (
                  <KeepColumnCell
                    radio={{
                      label: `Keep ${entry.chemicalName}`,
                      testId: `inventory-overlap-keep-${key}-${entry.chemicalId}`,
                    }}
                    {...keepCellProps(entry.chemicalId)}
                  >
                    {entry.chemicalName}
                  </KeepColumnCell>
                ) : (
                  entry.chemicalName
                )}
              </TableCell>
            ))}
            <TableCell>
              <Typography variant="body2" color="text.secondary">
                → Merged inventory
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow sx={keepRowSx}>
            <TableCell component="th" scope="row">
              Amount
            </TableCell>
            {inventoryOverlap.entries.map((entry) => (
              <TableCell key={entry.chemicalId} sx={keepCellSx}>
                {isKeeping ? (
                  <KeepColumnCell {...keepCellProps(entry.chemicalId)}>
                    {formatEntryAmount(entry)}
                  </KeepColumnCell>
                ) : (
                  formatEntryAmount(entry)
                )}
              </TableCell>
            ))}
            <TableCell data-testid={`inventory-overlap-merged-amount-${key}`}>
              {renderMergedAmount(inventoryOverlap, resolution, selectedEntry)}
            </TableCell>
          </TableRow>
          <TableRow sx={keepRowSx}>
            <TableCell component="th" scope="row">
              Storage
            </TableCell>
            {inventoryOverlap.entries.map((entry) => (
              <TableCell key={entry.chemicalId} sx={keepCellSx}>
                {isKeeping ? (
                  <KeepColumnCell {...keepCellProps(entry.chemicalId)}>
                    {renderStorageLocations(entry.storageLocationNames)}
                  </KeepColumnCell>
                ) : (
                  renderStorageLocations(entry.storageLocationNames)
                )}
              </TableCell>
            ))}
            <TableCell data-testid={`inventory-overlap-merged-storage-${key}`}>
              {renderMergedStorage(inventoryOverlap, resolution, selectedEntry)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {isCombining &&
        (conflicts.storageLocations.length > 0 || conflicts.stateFieldConflicts.length > 0) && (
          <Stack spacing={1.5} sx={{ mt: 2 }} data-testid={`inventory-overlap-conflicts-${key}`}>
            <Typography variant="subtitle2">Resolve conflicting values before combining</Typography>
            {conflicts.storageLocations.map((location) => (
              <Box
                key={`${location.storageLocationId}|${JSON.stringify(location.storageTypes)}|${
                  location.storageTypeDescription ?? ""
                }`}
              >
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography variant="subtitle2">{location.storageLocationName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {storageTypesLabel(location.storageTypes, location.storageTypeDescription)}
                  </Typography>
                </Stack>
                {location.context.length > 0 && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    {location.context.map((c) => `${c.label}: ${c.display}`).join(" · ")}
                  </Typography>
                )}
                {location.conflicts.map((conflict) => {
                  const conflictKey = storageLocationConflictKey(location, conflict.attribute);
                  return (
                    <Box key={conflict.attribute} sx={indentedConflictRowSx}>
                      <Typography variant="body2">
                        {ATTRIBUTE_LABELS[conflict.attribute]}
                      </Typography>
                      {/* display: contents lifts each option into the shared grid columns. */}
                      <Box
                        role="radiogroup"
                        aria-label={ATTRIBUTE_LABELS[conflict.attribute]}
                        sx={{ display: "contents" }}
                      >
                        {conflict.options.map((option) => (
                          <SelectableOptionCell
                            key={option.serializedValue}
                            label={`${option.display} (${option.chemicalNames.join(", ")})`}
                            display={option.display}
                            caption={option.chemicalNames.join(", ")}
                            selected={
                              resolution.fieldChoices[conflictKey] === option.serializedValue
                            }
                            onSelect={() => onFieldChoice(conflictKey, option.serializedValue)}
                          />
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ))}
            {conflicts.storageLocations.length > 0 && conflicts.stateFieldConflicts.length > 0 && (
              <Divider />
            )}
            {conflicts.stateFieldConflicts.map((conflict) => {
              const conflictKey = stateFieldConflictKey(conflict.jurisdiction, conflict.key);
              const groupLabel = `${conflict.label} (${conflict.jurisdiction})`;
              return (
                <Box key={conflictKey} sx={{ ...conflictRowSx, mt: 0 }}>
                  <Typography variant="body2">{groupLabel}</Typography>
                  <Box role="radiogroup" aria-label={groupLabel} sx={{ display: "contents" }}>
                    {conflict.options.map((option) => (
                      <SelectableOptionCell
                        key={option.chemicalId}
                        label={`${option.display} (${option.chemicalName})`}
                        display={option.display}
                        caption={option.chemicalName}
                        selected={resolution.fieldChoices[conflictKey] === option.chemicalId}
                        onSelect={() => onFieldChoice(conflictKey, option.chemicalId)}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
    </Paper>
  );
}

export function ResolveInventoryOverlapsStep(props: ResolveInventoryOverlapsStepProps) {
  const allResolved = props.inventoryOverlaps.every((inventoryOverlap) =>
    isInventoryOverlapResolved(
      inventoryOverlap,
      props.resolutions[inventoryOverlapKey(inventoryOverlap)]
    )
  );

  const facilityGroups = Object.values(groupBy(props.inventoryOverlaps, "facilityId"));

  const openOverlapStrategies = new Set(
    props.inventoryOverlaps
      .filter((inventoryOverlap) => !inventoryOverlap.isFiled)
      .map(
        (inventoryOverlap) =>
          props.resolutions[inventoryOverlapKey(inventoryOverlap)]?.strategy ?? null
      )
  );
  const batchStrategyValue =
    openOverlapStrategies.size === 1 ? (openOverlapStrategies.values().next().value ?? null) : null;

  return (
    <>
      {/* Match MergePickerContent's height so the dialog doesn't resize between steps. */}
      <DialogContent sx={{ height: "80vh", overflowY: "auto" }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            <b>{props.candidateCount} catalog entries → 1.</b>{" "}
            {props.inventoryOverlaps.length === 1
              ? "1 inventory overlap needs a decision."
              : `${props.inventoryOverlaps.length} inventory overlaps need a decision.`}
            {props.autoMergeFacilityYearCount > 0 && (
              <>
                {" "}
                {props.autoMergeFacilityYearCount === 1
                  ? "The other inventory merges automatically."
                  : `The other ${props.autoMergeFacilityYearCount} inventories merge automatically.`}
              </>
            )}
          </Alert>
          {props.inventoryOverlaps.length > 1 &&
            props.inventoryOverlaps.some((inventoryOverlap) => !inventoryOverlap.isFiled) && (
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                <Typography variant="caption" color="text.secondary">
                  Set all not-yet-filed years to:
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={batchStrategyValue}
                  onChange={(_event, strategy) => {
                    if (strategy) props.onBatchStrategyChange(strategy);
                  }}
                  data-testid="inventory-overlap-batch-strategy"
                >
                  <ToggleButton value={InventoryOverlapResolutionStrategy.Keep}>
                    Keep one
                  </ToggleButton>
                  <ToggleButton value={InventoryOverlapResolutionStrategy.Combine}>
                    Combine
                  </ToggleButton>
                  <ToggleButton value={InventoryOverlapResolutionStrategy.Blank}>
                    Leave blank
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            )}
          {facilityGroups.map((groupOverlaps) => (
            <Stack key={groupOverlaps[0].facilityId} spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FactoryIcon fontSize="small" color="action" />
                <Typography variant="subtitle1" fontWeight="bold">
                  {groupOverlaps[0].facilityName}
                </Typography>
              </Stack>
              <Stack spacing={2}>
                {groupOverlaps.map((inventoryOverlap) => {
                  const key = inventoryOverlapKey(inventoryOverlap);
                  const resolution = props.resolutions[key] ?? {
                    strategy: InventoryOverlapResolutionStrategy.Keep,
                    fieldChoices: {},
                  };
                  return (
                    <InventoryOverlapCard
                      key={key}
                      inventoryOverlap={inventoryOverlap}
                      resolution={resolution}
                      onStrategyChange={(strategy) => props.onStrategyChange(key, strategy)}
                      onKeepSelect={(keepChemicalId) => props.onKeepSelect(key, keepChemicalId)}
                      onFieldChoice={(conflictKey, choice) =>
                        props.onFieldChoice(key, conflictKey, choice)
                      }
                    />
                  );
                })}
              </Stack>
            </Stack>
          ))}
          <DialogContentText component="div">
            <Typography component="p">
              You are about to create a new merged chemical for <i>{props.mergedChemicalName}</i>.
              This action will merge the selected chemicals into a single merged chemical in your
              chemical catalog and update all references to those chemicals in your facility
              inventory.{" "}
              <b>
                {props.affectedFacilityCount} {pluralize("facility", props.affectedFacilityCount)}{" "}
                will be affected.
              </b>
            </Typography>
            <Typography component="p" sx={{ mt: 1 }}>
              This action will <b>not</b> affect previously submitted reports. Dropped entries
              remain available in the merge audit record.
            </Typography>
          </DialogContentText>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={props.onBack}
          variant="outlined"
          data-testid="resolve-inventory-overlaps-back"
        >
          BACK
        </Button>
        <Button
          variant="contained"
          disabled={!allResolved || props.isMerging}
          loading={props.isMerging}
          onClick={props.onMerge}
          data-testid="resolve-inventory-overlaps-merge"
        >
          <MergeIcon /> MERGE
        </Button>
      </DialogActions>
    </>
  );
}
